// Tree row numbering + ordering helpers.
//
// Two things the tree needs and both trees (ProjectTree + the store's builder)
// have to agree on, or the same folder renders differently on two pages:
//
//   1. `splitLeadingNumber` — some workspaces bake an ordering prefix into the
//      stored name ("01-Finance", "101 — Create Customer"). Those rows should
//      show the number in the tree's narrow number gutter, not repeat it inside
//      the label next to a *computed* number. So we pull the prefix out of the
//      label and hand it to the gutter; rows without one get their sibling
//      position instead. Either way there is exactly one number per row.
//
//   2. `naturalCompare` — "10" must sort after "2", not between "1" and "3".
//      Guides carry no ordinal column (unlike projects), so a numbered title is
//      the only ordering signal they have; a plain localeCompare would render a
//      numbered folder in a nonsense order.

/** Leading "12-", "12.", "12)", "12 —", "12 –" — up to 3 digits, separator required
 *  so a title that merely *starts* with a year or a count ("2026 review") is left
 *  alone. The separator may be a hyphen, dot, paren, en/em dash. */
const LEADING_NUMBER = /^\s*(\d{1,3})\s*[-–—.)]\s*(.+)$/;

export interface SplitLabel {
  /** The prefix number, if the label carried one. Not zero-padded. */
  num?: string;
  /** The label with any leading number + separator removed. Never empty. */
  rest: string;
}

export function splitLeadingNumber(label: string): SplitLabel {
  const m = LEADING_NUMBER.exec(label);
  if (!m) return { rest: label };
  const rest = m[2].trim();
  // A label that is *only* a number ("01-") keeps its original text rather than
  // collapsing to an empty row.
  if (!rest) return { rest: label };
  return { num: m[1], rest };
}

/** Zero-pad to at least 2 digits: 1 → "01", 12 → "12", 101 → "101". */
export function pad2(n: number | string): string {
  return String(n).padStart(2, '0');
}

/**
 * The number shown in a row's gutter: the label's own baked prefix when it has
 * one, otherwise its 1-based position among its siblings.
 */
export function gutterNumber(label: string, siblingIndex: number): { displayNumber: string; label: string } {
  const { num, rest } = splitLeadingNumber(label);
  return {
    displayNumber: pad2(num ?? siblingIndex + 1),
    label: rest,
  };
}

// `numeric` makes "2" < "10"; `sensitivity: 'base'` makes the sort
// case-insensitive so "apple" and "Apple" don't straddle "Banana".
const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

export function naturalCompare(a: string, b: string): number {
  return collator.compare(a, b);
}

/**
 * Guide ordering inside a folder.
 *
 * BP 54 — guides now carry a user-controlled `ordinal` (drag-to-reorder on the
 * Projects Tree). It is the PRIMARY key. Every guide defaults to `ordinal = 0`
 * (migration 142 backfills nothing), so a folder the user has never dragged in
 * has all-equal ordinals and falls straight through to the legacy ordering
 * below — a numbered title prefix ("01-", "02-"), then newest-first for the
 * untitled/unnumbered case (what an inbox of fresh captures wants). The moment
 * a guide is dragged, POST /guides/:id/move stamps that folder's guides with
 * dense 0..N-1 ordinals and they honour the explicit drag order instead.
 */
export function compareGuides(
  a: { title: string; created_at?: string; ordinal?: number },
  b: { title: string; created_at?: string; ordinal?: number },
): number {
  // Explicit drag order wins. Equal ordinals (the untouched-folder case) fall
  // through to the numbered-title / newest-first tiebreak below.
  const oa = a.ordinal ?? 0;
  const ob = b.ordinal ?? 0;
  if (oa !== ob) return oa - ob;

  const na = splitLeadingNumber(a.title || '').num;
  const nb = splitLeadingNumber(b.title || '').num;
  // Numbered snips always sort above unnumbered ones, ascending by number.
  if (na && nb) {
    const d = Number(na) - Number(nb);
    if (d !== 0) return d;
  } else if (na || nb) {
    return na ? -1 : 1;
  } else {
    const ta = a.created_at ? Date.parse(a.created_at) : 0;
    const tb = b.created_at ? Date.parse(b.created_at) : 0;
    if (tb !== ta) return tb - ta;
  }
  return naturalCompare(a.title || '', b.title || '');
}
